import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

type Quality = 'good' | 'fair' | 'poor';

/**
 * Displays connection quality based on RTT (round-trip time).
 * Good: <100ms, Fair: <250ms, Poor: >=250ms
 */
@customElement('connection-quality')
export class ConnectionQuality extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
                justify-content: center;
                margin-top: 0.5rem;
            }

            .label {
                color: var(--text-secondary);
                font-size: 0.875rem;
                font-weight: 500;
            }

            .indicator {
                font-size: 1.25rem;
                line-height: 1;
            }

            .indicator.good {
                color: var(--success-color);
            }

            .indicator.fair {
                color: var(--warning-color);
            }

            .indicator.poor {
                color: var(--danger-color);
            }

            .rtt-value {
                color: var(--text-primary);
                font-size: 0.875rem;
                font-weight: 600;
                min-width: 3.5rem;
                text-align: center;
                padding: 0.25rem 0.5rem;
                background-color: var(--surface-light);
                border-radius: 0.25rem;
            }
        `,
    ];

    @property({ type: Number })
    rtt = 0;

    private get _quality(): Quality {
        if (this.rtt < 100) return 'good';
        if (this.rtt < 250) return 'fair';
        return 'poor';
    }

    render() {
        const quality = this._quality;
        return html`
            <span class="label">Connection:</span>
            <span class="indicator ${quality}">\u25CF</span>
            <span class="rtt-value">${this.rtt > 0 ? `${this.rtt} ms` : '-- ms'}</span>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'connection-quality': ConnectionQuality;
    }
}
